import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Bug, Lightbulb, MessageSquare, Monitor, Globe, User } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"

const typeConfig = {
  bug: { label: "Bug", icon: Bug, className: "bg-red-50 text-red-700 border-red-200" },
  feature_request: {
    label: "Feature Request",
    icon: Lightbulb,
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  other: {
    label: "Other",
    icon: MessageSquare,
    className: "bg-zinc-100 text-zinc-700 border-zinc-200",
  },
}

const statusConfig = {
  submitted: { label: "Submitted", className: "bg-amber-50 text-amber-700 border-amber-200" },
  working_on: { label: "Working On", className: "bg-blue-50 text-blue-700 border-blue-200" },
  completed: { label: "Completed", className: "bg-green-50 text-green-700 border-green-200" },
}

const statusOrder = ["submitted", "working_on", "completed"]

function MetaRow({ label, value }) {
  if (!value) return null
  return (
    <div className="flex gap-3">
      <span className="text-xs text-zinc-400 w-24 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-zinc-700 break-all">{value}</span>
    </div>
  )
}

export default function FeedbackShow({ currentUser, submission }) {
  const config = typeConfig[submission.feedbackType] || typeConfig.other
  const Icon = config.icon

  const formattedDate = new Date(submission.createdAt).toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })

  return (
    <AdminLayout currentUser={currentUser}>
      <Head title={`Feedback #${submission.id} — Admin`} />

      <div className="max-w-3xl space-y-6">
        {/* Back link */}
        <Link
          href="/app/admin/feedback_submissions"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
        >
          <ArrowLeft size={14} />
          All Feedback
        </Link>

        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <Badge variant="outline" className={`gap-1 ${config.className}`}>
                <Icon size={11} />
                {config.label}
              </Badge>
              <div className="flex items-center gap-1">
                {statusOrder.map((s) => {
                  const sc = statusConfig[s]
                  const isActive = submission.status === s
                  return (
                    <Badge
                      key={s}
                      variant="outline"
                      className={`cursor-pointer transition-opacity ${isActive ? sc.className : "bg-zinc-50 text-zinc-400 border-zinc-200 opacity-50 hover:opacity-75"}`}
                      onClick={() => {
                        if (!isActive) {
                          router.patch(`/app/admin/feedback_submissions/${submission.id}`, { status: s }, { preserveScroll: true })
                        }
                      }}
                    >
                      {sc.label}
                    </Badge>
                  )
                })}
              </div>
              <span className="text-xs text-zinc-400">{formattedDate}</span>
            </div>
            <p className="text-lg font-semibold text-zinc-900 leading-snug">
              #{submission.id} — {submission.pageTitle || submission.pageUrl}
            </p>
          </div>
        </div>

        {/* Description */}
        <Card>
          <CardHeader className="pb-2">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Description</p>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">
              {submission.description}
            </p>
          </CardContent>
        </Card>

        {/* Screenshot */}
        {submission.screenshotUrl && (
          <Card>
            <CardHeader className="pb-2">
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Screenshot</p>
            </CardHeader>
            <CardContent>
              <a href={submission.screenshotUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={submission.screenshotUrl}
                  alt="Feedback screenshot"
                  className="w-full rounded-lg border border-zinc-200 hover:opacity-90 transition-opacity cursor-zoom-in"
                />
              </a>
              <p className="text-xs text-zinc-400 mt-2">Click to open full size</p>
            </CardContent>
          </Card>
        )}

        {/* Two-column metadata */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Submitter */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-1.5">
                <User size={13} className="text-zinc-400" />
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Submitted by</p>
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-sm font-medium text-zinc-800">{submission.submittedBy.name}</p>
              <p className="text-xs text-zinc-400">{submission.submittedBy.email}</p>
            </CardContent>
          </Card>

          {/* Page info */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-1.5">
                <Globe size={13} className="text-zinc-400" />
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Page</p>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {submission.pageTitle && (
                <p className="text-sm text-zinc-700">{submission.pageTitle}</p>
              )}
              <p className="text-xs text-zinc-400 break-all">{submission.pageUrl}</p>
            </CardContent>
          </Card>
        </div>

        {/* Device info */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-1.5">
              <Monitor size={13} className="text-zinc-400" />
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Device & Browser</p>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {submission.screenWidth && (
              <MetaRow
                label="Screen"
                value={`${submission.screenWidth} × ${submission.screenHeight}px`}
              />
            )}
            {submission.viewportWidth && (
              <MetaRow
                label="Viewport"
                value={`${submission.viewportWidth} × ${submission.viewportHeight}px`}
              />
            )}
            {submission.userAgent && (
              <MetaRow label="User Agent" value={submission.userAgent} />
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}
